import React, {useCallback, useEffect, useMemo, useState} from "react";
import {numComparator} from "../../../../../../../../core/services/utils/utils";
import Grid2 from "@mui/material/Unstable_Grid2";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import {ConfigurationDataTypes, ConfigurationTypes} from "../../../../../../../../core/constants/enums";
import usePartnerConfiguration from "../../../../../../../hooks/use-partner-configuration";

/**
 * Organizational (partner-scoped) dynamic configuration fields from the shared partner-configuration context.
 */
export const DynamicOrganizationalConfigurationsSection = () => {
    const {
        properties,
        loading,
        getPartnerValue,
        setPartnerValue,
        fetchConfigurations,
    } = usePartnerConfiguration();
    const [savingPropertyIds, setSavingPropertyIds] = useState(() => new Set());

    /**
     * Ensures the shared configuration catalog is loaded when the settings shell mounts.
     */
    useEffect(() => {
        void fetchConfigurations({useCached: true});
    }, [fetchConfigurations]);

    const configGroups = useMemo(() => {
        const scoped = properties
            .filter((property) => property.isActive !== false)
            .filter((property) =>
                property.typeId === ConfigurationTypes.organization
                || property.typeId === ConfigurationTypes.both
            )
            .slice()
            .sort((a, b) => numComparator(a.index, b.index));

        const groups = scoped.reduce((prev, current) => {
            if (!prev[current.groupId]) {
                prev[current.groupId] = [];
            }
            prev[current.groupId].push({
                ...current,
                configurationValue: getPartnerValue(current.id),
                loading: savingPropertyIds.has(current.id),
            });
            return prev;
        }, {});

        return Object.entries(groups).map(([key, groupProperties]) => ({
            id: Number(key),
            properties: groupProperties,
            title: groupProperties[0]?.groupTitle,
        }));
    }, [getPartnerValue, properties, savingPropertyIds]);

    /**
     * Persists a partner-level configuration value.
     */
    const updateConfigValue = useCallback(async (propertyId, value) => {
        setSavingPropertyIds((prev) => new Set(prev).add(propertyId));
        await setPartnerValue(propertyId, value);
        setSavingPropertyIds((prev) => {
            const next = new Set(prev);
            next.delete(propertyId);
            return next;
        });
    }, [setPartnerValue]);

    return (
        <>
            {
                loading && !properties.length &&
                Array.from({length: 2}).map((_, index) => (
                    <Grid2 xs={12} key={index}>
                        <div className={'loading access-level w-100'}>
                            <div>
                                <div/>
                            </div>
                        </div>
                    </Grid2>
                ))
            }
            {
                (!loading || !!properties.length)
                && configGroups?.map((group) => (
                    <Grid2 xs={12} key={group.id}>
                        <ConfigurationGroup
                            group={group}
                            onChange={updateConfigValue}
                        />
                    </Grid2>
                ))
            }
        </>
    );
}


const ConfigurationGroup = ({group, onChange}) => {
    /**
     * Maps the property data type to ConfigurableBlockInputTypes.
     */
    const getInputType = useCallback((property) => {
        switch (property.dynamicDataTypeId) {
            case ConfigurationDataTypes.boolean:
                return ConfigurableInfoBlockInputTypes.toggle;
            case ConfigurationDataTypes.list:
                return ConfigurableInfoBlockInputTypes.select;
            case ConfigurationDataTypes.string:
            case ConfigurationDataTypes.number:
            default:
                return ConfigurableInfoBlockInputTypes.input;
        }
    }, [])

    /**
     * Builds input props for the given configuration property.
     */
    const getInputProps = useCallback((property) => {
        const valueRaw = property.configurationValue;
        switch (property.dynamicDataTypeId) {
            case ConfigurationDataTypes.number:
                return {
                    value: valueRaw ?? '',
                    type: "number",
                    placeholder: 'e.g. 123',
                };
            case ConfigurationDataTypes.boolean:
                return {
                    value: valueRaw === true || valueRaw === 'true' || valueRaw === '1'
                        ? 'true'
                        : valueRaw === false || valueRaw === 'false' || valueRaw === '0'
                            ? 'false'
                            : valueRaw,
                    exclusive: true,
                    data: ['true', 'false'].map(e => ({
                        value: e,
                        name: e === 'true' ? "Yes" : "No",
                    }))
                }
            case ConfigurationDataTypes.list: {
                const options = property.defaultValues ?? [];
                const selected = options.find(e => e.id === Number(valueRaw)) ?? null;
                return {
                    value: selected,
                    data: options.map(e => ({
                        value: e,
                        label: e.title,
                        id: e.id,
                    })),
                }
            }
            case ConfigurationDataTypes.string:
            default:
                return {
                    value: valueRaw ?? '',
                    type: "text",
                    placeholder: 'e.g. text',
                };
        }
    }, [])

    /**
     * Commits the edited value through SetPartner.
     */
    const changeValue = useCallback(async (property, newValue) => {
        let valueToSave = null;
        switch (property.dynamicDataTypeId) {
            case ConfigurationDataTypes.boolean:
                if (newValue === null || newValue === undefined) {
                    return;
                }
                if (String(property.configurationValue) === String(newValue)
                    || (property.configurationValue === true && newValue === 'true')
                    || (property.configurationValue === false && newValue === 'false')
                    || (property.configurationValue === '1' && newValue === 'true')
                    || (property.configurationValue === '0' && newValue === 'false')) {
                    return;
                }
                valueToSave = String(newValue);
                break;
            case ConfigurationDataTypes.list:
                if (property.configurationValue === newValue?.id?.toString()) {
                    return;
                }
                valueToSave = newValue?.id != null ? String(newValue.id) : null;
                break;
            case ConfigurationDataTypes.number:
            case ConfigurationDataTypes.string:
            default:
                if (property.configurationValue === newValue) {
                    return;
                }
                valueToSave = !newValue?.length ? null : newValue;
                break;
        }
        await onChange(property.id, valueToSave);
    }, [onChange])

    return (
        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
            <Grid2 container spacing={2}>
                <Grid2 xs={12}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        {group.title ?? ""}
                    </Typography>
                </Grid2>
                <Grid2 xs={12}>
                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                </Grid2>
                {
                    group.properties?.map(property => (
                        <Grid2 xs={12} lg={6} key={property.id} paddingInline={3}>
                            <ConfigurableInfoBlock
                                title={property.name}
                                inputType={getInputType(property)}
                                inputProps={getInputProps(property)}
                                onValueChanged={(value) => changeValue(property, value)}
                                loading={property.loading}
                                showCustomIcons
                                customIcons={[
                                    <Tooltip title={property.description} key={1}>
                                        <InfoOutlined color={'primary'}/>
                                    </Tooltip>
                                ]}
                            />
                        </Grid2>
                    ))
                }
            </Grid2>
        </Card>
    );
}
